import { useCallback, useEffect, useRef, useState } from 'react';
import { requestTyped } from '@xdocs/ws-protocol';
import { explorerEventPush, validateUploadConstraints, wsChannel } from '@xdocs/schemas';
import type { UploadFileResult } from '@xdocs/schemas';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';
import { resolveUploadUrl, uploadOneFile, UploadTransportError } from './uploadTransport.js';
import type { UploadsMessageId } from './useUploadsT.js';
import { useUploadsT } from './useUploadsT.js';

export type UploadQueueItemStatus = 'validating' | 'queued' | 'uploading' | 'uploaded' | 'ingesting' | 'ready' | 'failed';

export interface UploadQueueItem {
  id: string;
  file: File;
  status: UploadQueueItemStatus;
  /** 0-100, upload phase only (ingest has no byte-progress to report). */
  progress: number;
  /** `| undefined` kept explicit alongside `?:` (`exactOptionalPropertyTypes`
   * — see `apps/web/src/lib/optionalProp.ts`'s docstring for the convention):
   * clearing an error on retry/re-upload assigns `undefined` rather than
   * omitting the key. */
  errorMessage?: string | undefined;
  /** Set once the object lands in S3 (`uploads.fileId`) — drives ingest tracking. */
  fileId: string | null;
  canRetry: boolean;
}

export interface UseUploadQueueResult {
  items: UploadQueueItem[];
  addFiles: (files: FileList | readonly File[]) => void;
  retry: (itemId: string) => void;
  remove: (itemId: string) => void;
  isUploading: boolean;
  readyCount: number;
  inProgressCount: number;
}

const MAX_CONCURRENT_UPLOADS = 3;

function nextId(): string {
  return typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

function mimeErrorMessageId(path: string | undefined, t: (id: UploadsMessageId) => string): string {
  if (path === 'byteSize') return t('uploads:error.size.tooLarge');
  return t('uploads:error.mimeType.unsupported');
}

function transportErrorMessage(err: unknown, t: (id: UploadsMessageId) => string): string {
  if (err instanceof UploadTransportError) {
    if (err.reason === 'network') return t('uploads:error.network');
    if (err.reason === 'aborted') return t('uploads:error.network');
  }
  return t('uploads:error.network');
}

/**
 * Client-side upload queue (F18): drag-drop/picker feed `addFiles`, which
 * client-side pre-validates each file with the SAME `validateUploadConstraints`
 * `@xdocs/schemas` exports (E6 — one schema, never a re-implemented "looser"
 * client copy) before it ever touches the network, uploads with bounded
 * concurrency + per-file progress via `uploadTransport.ts`, then tracks the
 * post-upload ingest lifecycle live off the `user:{userId}` explorer-event
 * channel (F7's `explorerEventPush` — no per-upload WS op exists, see this
 * task's returned open issues) rather than fabricating a job id that was
 * never actually returned by `POST /upload`.
 */
export function useUploadQueue(folderId: string | null): UseUploadQueueResult {
  const { accessToken, userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const { t } = useUploadsT();
  const [items, setItems] = useState<UploadQueueItem[]>([]);
  const controllersRef = useRef(new Map<string, AbortController>());
  const itemsRef = useRef<UploadQueueItem[]>([]);
  itemsRef.current = items;

  function patchItem(id: string, patch: Partial<UploadQueueItem>): void {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }

  const runUpload = useCallback(
    (item: UploadQueueItem) => {
      if (!accessToken) {
        patchItem(item.id, { status: 'failed', errorMessage: t('uploads:error.network'), canRetry: true });
        return;
      }
      const controller = new AbortController();
      controllersRef.current.set(item.id, controller);
      patchItem(item.id, { status: 'uploading', progress: 0, errorMessage: undefined });

      uploadOneFile({
        url: resolveUploadUrl(),
        accessToken,
        file: item.file,
        folderId,
        signal: controller.signal,
        onProgress: (event) => {
          const pct = event.total > 0 ? Math.round((event.loaded / event.total) * 100) : 0;
          patchItem(item.id, { progress: pct });
        },
      })
        .then((response) => {
          controllersRef.current.delete(item.id);
          const result: UploadFileResult | undefined = response.files[0];
          if (!result) {
            patchItem(item.id, { status: 'failed', errorMessage: t('uploads:error.network'), canRetry: true });
            return;
          }
          if (result.ok) {
            patchItem(item.id, {
              status: 'ingesting',
              progress: 100,
              fileId: result.upload.fileId,
              canRetry: false,
            });
          } else {
            patchItem(item.id, { status: 'failed', errorMessage: result.error.message, canRetry: true });
          }
        })
        .catch((err: unknown) => {
          controllersRef.current.delete(item.id);
          const current = itemsRef.current.find((candidate) => candidate.id === item.id);
          if (current?.status !== 'uploading') return; // removed/canceled meanwhile
          patchItem(item.id, { status: 'failed', errorMessage: transportErrorMessage(err, t), canRetry: true });
        });
    },
    [accessToken, folderId, t],
  );

  // Bounded-concurrency runner: whenever the queue changes, top up in-flight
  // uploads to MAX_CONCURRENT_UPLOADS from the front of the 'queued' items.
  // Re-runs on every `items` change by design (queue drain loop);
  // `runUpload` is stable per accessToken/folderId — intentionally
  // dep-limited (`react-hooks/exhaustive-deps` isn't registered for plain
  // `.ts` files under this repo's ESLint config, `.jsx`/`.tsx` only, so no
  // disable directive is valid/needed here).
  useEffect(() => {
    const uploading = items.filter((item) => item.status === 'uploading').length;
    const slots = MAX_CONCURRENT_UPLOADS - uploading;
    if (slots <= 0) return;
    const queued = items.filter((item) => item.status === 'queued').slice(0, slots);
    for (const item of queued) runUpload(item);
  }, [items]);

  // Live ingest tracking (F7 explorer-event convention — "signal, not
  // payload"): re-derive status from the event enum directly; on a terminal
  // 'failed' event, one targeted `file.get` fetches the human-readable
  // `uploads.parse_error` (E12) the push itself doesn't carry.
  useEffect(() => {
    if (!userId || connectionState !== 'connected') return undefined;
    const channel = wsChannel.user(userId);
    return wsClient.subscribe(channel, (envelope) => {
      const parsed = explorerEventPush.safeParse(envelope.payload);
      if (!parsed.success || parsed.data.entryType !== 'file') return;
      const { event, id: pushedFileId } = parsed.data;
      const match = itemsRef.current.find((item) => item.fileId === pushedFileId);
      if (!match) return;

      if (event === 'ready') {
        patchItem(match.id, { status: 'ready' });
      } else if (event === 'processing') {
        patchItem(match.id, { status: 'ingesting' });
      } else if (event === 'failed') {
        void requestTyped(wsClient, 'file.get', { fileId: pushedFileId })
          .then((entry) => {
            const message = entry.type === 'file' && entry.errorMessage ? entry.errorMessage : t('uploads:error.parse.generic');
            patchItem(match.id, { status: 'failed', errorMessage: message, canRetry: false });
          })
          .catch(() => {
            patchItem(match.id, { status: 'failed', errorMessage: t('uploads:error.parse.generic'), canRetry: false });
          });
      }
    });
  }, [wsClient, userId, connectionState, t]);

  const addFiles = useCallback(
    (files: FileList | readonly File[]) => {
      const list = Array.from(files);
      setItems((prev) => {
        const additions: UploadQueueItem[] = list.map((file) => {
          const alert = validateUploadConstraints({ contentType: file.type, byteSize: file.size });
          if (alert) {
            return {
              id: nextId(),
              file,
              status: 'failed',
              progress: 0,
              errorMessage: mimeErrorMessageId(alert.fields?.[0]?.path, t),
              fileId: null,
              canRetry: false,
            };
          }
          return { id: nextId(), file, status: 'queued', progress: 0, fileId: null, canRetry: false };
        });
        return [...prev, ...additions];
      });
    },
    [t],
  );

  const retry = useCallback((itemId: string) => {
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, status: 'queued', progress: 0, errorMessage: undefined } : item)),
    );
  }, []);

  const remove = useCallback((itemId: string) => {
    const controller = controllersRef.current.get(itemId);
    if (controller) {
      controller.abort();
      controllersRef.current.delete(itemId);
    }
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      for (const controller of controllers.values()) controller.abort();
    };
  }, []);

  const isUploading = items.some((item) => item.status === 'uploading' || item.status === 'validating');
  const readyCount = items.filter((item) => item.status === 'ready').length;
  const inProgressCount = items.filter((item) => item.status === 'uploading' || item.status === 'ingesting' || item.status === 'queued').length;

  return { items, addFiles, retry, remove, isUploading, readyCount, inProgressCount };
}
